import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Toaster } from '@beyond-js/ui';
import { useI18n } from '@cdn/i18n';

type Show = (message: string) => void;

const Context = createContext<Show | undefined>(undefined);

/**
 * The one region of outcome messages of the page (the family's toaster). A confirmed destructive or
 * irreversible action says what happened with its own verb in the past tense ("Release 3 retired"), after
 * its dialog has closed; a toast is never stored and is not a notification.
 *
 * The region is created with the first outcome, so a page that shows none carries no empty live regions, and
 * the message is placed a frame later, once the region exists, so assistive technology announces it.
 */
export function OutcomeProvider({ children }: { children: ReactNode }) {
	const { locale } = useI18n();
	const toaster = useRef<Toaster>();
	const language = useRef(locale.code);
	language.current = locale.code;

	useEffect(() => () => toaster.current?.destroy(), []);

	const show = useMemo<Show>(
		() => message => {
			if (!toaster.current) {
				toaster.current = new Toaster({ labels: Toaster.labels[language.current === 'es' ? 'es' : 'en'] });
				toaster.current.mount(document.body);
			}
			const region = toaster.current;
			requestAnimationFrame(() => region.show(message, { tone: 'success' }));
		},
		[]
	);
	return <Context.Provider value={show}>{children}</Context.Provider>;
}

/**
 * Shows the outcome of an action; without a provider (a screen rendered alone in a test) it does nothing
 */
export function useOutcome(): Show {
	return useContext(Context) ?? (() => undefined);
}
